/**
 * The app's half of `torik-hub-challenge-v1` — proving a LAN responder is the
 * hub the account pinned (card `t_4a53bf5c`, plan
 * `docs/plans/LOCAL-FIRST-CONNECT.md` §2/D6).
 *
 * Why this module exists, in one sentence: `GET /api/hub/identity` is plaintext,
 * so the `publicId`/`publicKey` pair behind the local-route match can be copied
 * by anything on the network — and the next thing this app does on that route is
 * hand the responder the **account's bearer token** (`POST /api/auth/claim`).
 * The hub's own `publicId` proves *nothing* about who answered; a signature over
 * a nonce this app chose does.
 *
 * ── What the app asks, and what it checks ───────────────────────────────────
 *
 * `POST <lan>/api/hub/identity/challenge` with `{nonce}` answers the hub's ids,
 * the nonce echoed back, the moment, and an Ed25519 signature over
 * (`otto/src/lib/hubChallenge.ts` owns the producing side, character for
 * character):
 *
 *     torik-hub-challenge-v1
 *     <nonce>
 *     <hubId>
 *     <publicId>
 *     <timestamp>
 *
 * The verdict is only `ok` when **all** of these hold:
 *
 *   - the answer's `publicKey` is the key the ACCOUNT pinned
 *     (`GET /api/hubs/:id/bindings` → `hubPublicKey`, read by
 *     `services/hubAccountIdentity.ts`) — compared before any cryptography, so a
 *     responder that copied an identity is refused without even a hash;
 *   - the answer's `publicId` is the account's own for that hub;
 *   - the nonce in the answer is the one this call made — an answer to someone
 *     else's question is not an answer to this one;
 *   - the signature verifies over the canonical string, with the pinned key,
 *     through WebCrypto (`crypto.subtle`, Ed25519 — Chromium 113+ / Electron /
 *     Node 18.4+, which every runtime this app ships on has).
 *
 * Anything else is `not-proven`, and the caller must do exactly one thing with
 * it: not send the token (the tunnel is the fallback — `services/hubConnect.ts`).
 *
 * ── The ladder, and why the verdict carries it ──────────────────────────────
 *
 * A hub is asked on the same address ladder discovery resolved it on
 * (`hubApiAttempts`, shared with `services/hubIdentityLookup.ts`), because a hub
 * behind TLS on `:443` and a legacy raw API on `:16161` are one hub with two
 * doors. The rung that *proved the key* is returned with the verdict, and the
 * claim is dialled at **that** rung alone: a proof collected at one address must
 * never license a token to another.
 *
 * ── The second door, and why it needs a second function ─────────────────────
 *
 * Adoption (`POST /api/hub/adopt`, card `t_515b4051`) hands the account's bearer
 * token over as well, and there the account has nothing pinned to check against:
 * the hub it is adopting is one it has never met (the cloud row is created by
 * that very call). So `proveResponderKey` below asks the same two questions of
 * the responder's own claim — the id it names must be the hash of the key it
 * names, and it must sign this call's nonce with that key — and the caller takes
 * the same rule away: the token goes to the rung that proved, and nowhere else.
 *
 * Nothing here stores anything, opens a dialog or knows about Vue: it fetches,
 * verifies, and answers a value.
 */
import { isHubPublicId, hubPublicIdFromKey, normalizeHubPublicId } from '../utils/hubIdentity';
import { hubApiAttempts, type HubApiAttempt } from './hubIdentityLookup';
import { resolveHubFetch, type HubFetch } from './hubFetch';

/** The hub path, relative to the ladder's `http(s)://<host>:<port>/api` base. */
export const HUB_CHALLENGE_PATH = '/hub/identity/challenge';

/** The domain tag: the first line of the canonical string, and its version. */
export const HUB_CHALLENGE_TAG = 'torik-hub-challenge-v1';

/** The one algorithm the answer may name. */
export const HUB_CHALLENGE_ALGORITHM = 'ed25519';

/** A fresh nonce is 18 random bytes → 24 base64url characters. */
export const HUB_CHALLENGE_NONCE_BYTES = 18;

/** The wire window the hub's parser accepts (`[A-Za-z0-9_-]`, 8..120). */
export const HUB_CHALLENGE_NONCE_MIN = 8;
export const HUB_CHALLENGE_NONCE_MAX = 120;

/** A raw Ed25519 public key is 32 bytes → 43 base64url characters, unpadded. */
const HUB_PUBLIC_KEY_PATTERN = /^[A-Za-z0-9_-]{43}$/;

const DEFAULT_TIMEOUT_MS = 2500;

// ─── The nonce ──────────────────────────────────────────────────────────────

/** Deterministic source of randomness, injectable so generation is testable. */
export type ChallengeRandomBytes = (length: number) => Uint8Array;

function systemRandomBytes(length: number): Uint8Array {
  const bytes = new Uint8Array(length);
  if (typeof globalThis.crypto?.getRandomValues === 'function') {
    globalThis.crypto.getRandomValues(bytes);
    return bytes;
  }
  throw new Error('No cryptographic random source available');
}

function base64url(bytes: Uint8Array): string {
  let binary = '';
  for (const byte of bytes) binary += String.fromCharCode(byte);
  const encoded = typeof btoa === 'function' ? btoa(binary) : Buffer.from(bytes).toString('base64');
  return encoded.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

/**
 * base64url → bytes, in the shape `crypto.subtle` accepts (`Uint8Array` over a
 * plain `ArrayBuffer`, the same type `services/hubFetch.ts` decodes into).
 */
function bytesFromBase64url(value: string): Uint8Array<ArrayBuffer> {
  const padded = value.replace(/-/g, '+').replace(/_/g, '/');
  const withPadding = padded + '='.repeat((4 - (padded.length % 4)) % 4);
  if (typeof atob === 'function') {
    const binary = atob(withPadding);
    const bytes = new Uint8Array(new ArrayBuffer(binary.length));
    for (let index = 0; index < binary.length; index += 1) bytes[index] = binary.charCodeAt(index);
    return bytes;
  }
  const fromBuffer = Buffer.from(withPadding, 'base64');
  const bytes = new Uint8Array(new ArrayBuffer(fromBuffer.length));
  bytes.set(fromBuffer);
  return bytes;
}

/**
 * A fresh nonce, in the alphabet the hub's parser accepts: 18 random bytes as
 * base64url, 24 characters — a value the responder cannot have seen, which is
 * what makes its signature an answer to *this* call and not a copy of an older
 * one.
 */
export function generateHubChallengeNonce(random: ChallengeRandomBytes = systemRandomBytes): string {
  const nonce = base64url(random(HUB_CHALLENGE_NONCE_BYTES));
  if (nonce.length < HUB_CHALLENGE_NONCE_MIN || nonce.length > HUB_CHALLENGE_NONCE_MAX) {
    throw new Error('The nonce did not have the length the format states');
  }
  return nonce;
}

// ─── The signed string ──────────────────────────────────────────────────────

/** The four fields the signature covers, exactly as the hub spells them. */
export interface HubChallengeParts {
  nonce: string;
  hubId: string;
  publicId: string;
  timestamp: string;
}

/**
 * The exact bytes the hub signed — built here the same way
 * `otto/src/lib/hubChallenge.ts` builds them, and the vector in
 * `hubChallenge.test.ts` is what keeps the two spellings equal.
 */
export function canonicalHubChallenge(parts: HubChallengeParts): string {
  return [HUB_CHALLENGE_TAG, parts.nonce, parts.hubId, parts.publicId, parts.timestamp].join('\n');
}

/** What `POST /api/hub/identity/challenge` answered. */
export interface HubChallengeAnswer extends HubChallengeParts {
  /** The key the responder claims — compared with the account's pinned one. */
  publicKey: string;
  algorithm: string;
  signature: string;
}

/**
 * Read the hub's answer. Null for anything that is not one — a captive portal, a
 * proxy's error page, a hub too old to know the route — because half an answer
 * must never be treated as proof of anything.
 */
export function readHubChallengeAnswer(body: unknown): HubChallengeAnswer | null {
  if (!body || typeof body !== 'object') return null;
  const raw = body as Record<string, unknown>;
  const publicId = normalizeHubPublicId(typeof raw.publicId === 'string' ? raw.publicId : null);
  const publicKey = typeof raw.publicKey === 'string' ? raw.publicKey.trim() : '';
  const nonce = typeof raw.nonce === 'string' ? raw.nonce : '';
  const timestamp = typeof raw.timestamp === 'string' ? raw.timestamp : '';
  const algorithm = typeof raw.algorithm === 'string' ? raw.algorithm.toLowerCase() : '';
  const signature = typeof raw.signature === 'string' ? raw.signature.trim() : '';

  if (!isHubPublicId(publicId)) return null;
  if (!HUB_PUBLIC_KEY_PATTERN.test(publicKey)) return null;
  if (!nonce || nonce.length > HUB_CHALLENGE_NONCE_MAX) return null;
  if (!/^\d{1,12}$/.test(timestamp)) return null;
  if (algorithm !== HUB_CHALLENGE_ALGORITHM) return null;
  if (!signature) return null;

  return {
    hubId: typeof raw.hubId === 'string' ? raw.hubId.trim() : '',
    publicId,
    publicKey,
    nonce,
    timestamp,
    algorithm,
    signature,
  };
}

// ─── The verdict ────────────────────────────────────────────────────────────

/**
 * Why an answer is not a proof. Each value is a different fact, and each one
 * ends the same way: no token leaves the app.
 */
export type HubChallengeRefusal =
  /** The answer was not a challenge answer at all (or had no usable fields). */
  | 'malformed'
  /** The responder's key is not the one the account pinned. */
  | 'key-mismatch'
  /** The responder names a different hub than the account's own. */
  | 'id-mismatch'
  /** The answer is to a nonce this app did not ask with. */
  | 'nonce-mismatch'
  /** The signature does not verify over the canonical string. */
  | 'invalid-signature'
  /** This runtime has no Ed25519 verifier (no `crypto.subtle`): unprovable. */
  | 'unsupported';

export type HubChallengeVerdict = { ok: true } | { ok: false; reason: HubChallengeRefusal };

export interface HubChallengeVerification {
  /** The answer as it arrived (null when nothing usable came back). */
  answer: HubChallengeAnswer | null;
  /** The nonce this call asked with. */
  nonce: string;
  /** The account's pinned public key (`GET /api/hubs/:id/bindings`). */
  publicKey: string;
  /** The account's pinned public id for the hub. */
  publicId: string;
}

/**
 * Verify one answer against the account's pinned key — the whole point of D6.
 *
 * Pure and async only because WebCrypto is: the same function is what a test
 * drives with a real signature, and what the connect path calls before it lets
 * the token leave.
 */
export async function verifyHubChallengeAnswer(input: HubChallengeVerification): Promise<HubChallengeVerdict> {
  const { answer, nonce, publicKey, publicId } = input;
  if (!answer) return { ok: false, reason: 'malformed' };

  // The account's own identity, first — no cryptography for a responder that
  // simply copied a key it does not hold.
  if (answer.publicKey !== publicKey) return { ok: false, reason: 'key-mismatch' };
  if (normalizeHubPublicId(answer.publicId) !== normalizeHubPublicId(publicId)) {
    return { ok: false, reason: 'id-mismatch' };
  }
  // The answer must be to THIS question. A captured answer to an older nonce
  // is a replay, and it is refused here rather than by the signature (which
  // would happily verify an old, valid answer).
  if (answer.nonce !== nonce) return { ok: false, reason: 'nonce-mismatch' };

  const subtle = globalThis.crypto?.subtle;
  if (!subtle || typeof subtle.importKey !== 'function') {
    return { ok: false, reason: 'unsupported' };
  }

  try {
    const key = await subtle.importKey('raw', bytesFromBase64url(publicKey), { name: 'Ed25519' }, false, ['verify']);
    const valid = await subtle.verify(
      { name: 'Ed25519' },
      key,
      bytesFromBase64url(answer.signature),
      new TextEncoder().encode(canonicalHubChallenge(answer)),
    );
    return valid ? { ok: true } : { ok: false, reason: 'invalid-signature' };
  } catch {
    // A malformed key, a browser without Ed25519, a signature that is not
    // bytes: all the same answer — this responder has not proven anything.
    return { ok: false, reason: 'unsupported' };
  }
}

// ─── Asking for the proof, over the address ladder ──────────────────────────

export interface HubKeyProofSuccess {
  ok: true;
  /** The rung that proved the key — where the claim is dialled. */
  baseUrl: string;
  /** The same rung, as the ladder spells it. */
  attempt: HubApiAttempt;
  /** The answer, already verified against the account's pinned key. */
  answer: HubChallengeAnswer;
}

export interface HubKeyProofFailure {
  ok: false;
  /**
   * `not-proven` — a responder answered and did not prove the pinned key
   * (a signature that does not verify, another key, another nonce, a body that
   * is not an answer at all). `cannot-prove` — no rung could be asked: no
   * challenge route here (a hub older than this release), a refusal, or no
   * answer at all.
   */
  reason: HubKeyProofRefusal;
  /** The last refusal, for the log line — never a sentence for a person. */
  detail: string | null;
}

/**
 * Why no proof arrived. The connect path carries these two values straight into
 * its own refusal vocabulary (`services/hubLocalFirst.ts` → `LocalRouteRefusal`)
 * — one fact, one name, in both modules.
 */
export type HubKeyProofRefusal = 'not-proven' | 'cannot-prove';

export type HubKeyProof = HubKeyProofSuccess | HubKeyProofFailure;

export interface ProveHubKeyInput {
  ip: string;
  port: number;
  /** The account's pinned public id for this hub (`GET /hubs/:id/bindings`). */
  publicId: string;
  /** The account's pinned public key for this hub — the trust anchor. */
  publicKey: string;
  /** Injected by tests; the runtime uses the hub's own transport. */
  fetchImpl?: HubFetch;
  /** Injected by tests; a deterministic ladder in place of the shared one. */
  attempts?: HubApiAttempt[];
  /** Per-request budget. */
  timeoutMs?: number;
}

/**
 * Ask the hub at this LAN address to prove it holds the key the account pinned.
 *
 * Every rung is asked until one proves it (a hub with two doors is one hub), and
 * the refusal is `not-proven` only when some rung actually *answered and failed*
 * — a rung that has no challenge route, refuses, or does not answer leaves the
 * verdict at `cannot-prove`, which is what an older hub gets.
 *
 * The nonce is fresh for each rung: a proof is one question, one answer.
 */
export async function proveHubKey(input: ProveHubKeyInput): Promise<HubKeyProof> {
  const doFetch = input.fetchImpl ?? resolveHubFetch();
  const timeoutMs = input.timeoutMs ?? DEFAULT_TIMEOUT_MS;
  const attempts = input.attempts ?? hubApiAttempts(input.port);

  let answeredAndFailed = false;
  let detail: string | null = null;

  for (const attempt of attempts) {
    const baseUrl = `${attempt.scheme}://${input.ip}:${attempt.port}/api`;
    const nonce = generateHubChallengeNonce();
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), timeoutMs);
    try {
      const response = await doFetch(`${baseUrl}${HUB_CHALLENGE_PATH}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ nonce }),
        signal: controller.signal,
      });

      // A rung without the route: a hub older than this release, or a front
      // door that does not proxy it. Not an answer, so not a refusal.
      if (response.status === 404 || response.status === 405) {
        detail = `${baseUrl}: no challenge route (${response.status})`;
        continue;
      }
      if (!response.ok) {
        // 403 (not on this network), 429 (rate limited), 503 (its key file is
        // unreadable): this hub would not answer — nothing was proven, and
        // nothing was disproven.
        detail = `${baseUrl}: refused (${response.status})`;
        continue;
      }

      const body = await response.json().catch(() => null);
      const answer = readHubChallengeAnswer(body);
      if (!answer) {
        answeredAndFailed = true;
        detail = `${baseUrl}: malformed`;
        continue;
      }
      const verdict = await verifyHubChallengeAnswer({
        answer,
        nonce,
        publicId: input.publicId,
        publicKey: input.publicKey,
      });
      if (verdict.ok) return { ok: true, baseUrl, attempt, answer };

      answeredAndFailed = true;
      detail = `${baseUrl}: ${verdict.reason}`;
    } catch (error) {
      // Timeout, refused connection, DNS — try the next address shape.
      detail = `${baseUrl}: ${error instanceof Error ? error.message : 'no answer'}`;
      continue;
    } finally {
      clearTimeout(timer);
    }
  }

  return { ok: false, reason: answeredAndFailed ? 'not-proven' : 'cannot-prove', detail };
}

// ─── The same proof, for a hub the ACCOUNT cannot name yet ───────────────────

export interface ResponderKeyProofSuccess {
  ok: true;
  /** The rung that proved the key — the only address a token may go to. */
  attempt: HubApiAttempt;
  /** The same rung as `http(s)://<ip>:<port>/api`. */
  baseUrl: string;
  /** The hub's own id, as the answer named it (empty string when it named none). */
  hubId: string;
  /** The proven identity: `publicId` **is** `sha256(publicKey)[0:16]`. */
  publicId: string;
  publicKey: string;
}

export interface ResponderKeyProofFailure {
  ok: false;
  /**
   * `not-proven` — a responder answered and did not prove the key it named (a
   * signature that does not verify, an id that is not the hash of the key, an
   * answer to someone else's nonce, a body that is not an answer at all).
   * `cannot-prove` — no rung could be asked: no challenge route here, a refusal,
   * or nothing at all.
   */
  reason: HubKeyProofRefusal;
  /**
   * Did anything at all answer over HTTP? True means the address is a responder
   * with a wrong answer; false means nothing is there. The two need different
   * sentences, and only this call can tell them apart.
   */
  answered: boolean;
  /** The last refusal, for the log line — never a sentence for a person. */
  detail: string | null;
}

export type ResponderKeyProof = ResponderKeyProofSuccess | ResponderKeyProofFailure;

export interface ProveResponderKeyInput {
  ip: string;
  port: number;
  /**
   * The identity the person's route named for this hub — a sticker's `h=`, or
   * the `publicId` of the card they clicked. When it is given, the proven
   * identity must be exactly it: an address that proves *a* key is not the same
   * address as the one the person chose.
   */
  expectPublicId?: string | null;
  /** Injected by tests; the runtime uses the hub's own transport. */
  fetchImpl?: HubFetch;
  /** Injected by tests; a deterministic ladder in place of the shared one. */
  attempts?: HubApiAttempt[];
  /** Per-request budget. */
  timeoutMs?: number;
}

/**
 * Ask a LAN responder to prove the key it *names* — for the one door where the
 * account has nothing pinned yet: **adoption** (card `t_515b4051`).
 *
 * `POST /api/hub/adopt` puts the account's bearer token in the request body, and
 * the account has no `hubPublicKey` for a hub it has not adopted (the cloud row
 * is created by that very call), so `proveHubKey`'s anchor — the account's own
 * record — does not exist yet. What does exist is the responder's own claim:
 * its `publicKey`, and the `publicId` it says is the hash of it. Two checks turn
 * that claim into a proof, and both are needed:
 *
 *   1. **the id is the key's hash** (`hubPublicIdFromKey`) — otherwise a
 *      responder could name a real hub's `publicId` and sign with a key of its
 *      own, which is the whole impostor trick in one line;
 *   2. **the signature verifies over this call's nonce with that key** — so
 *      whoever answered holds the private half, and cannot be a copy.
 *
 * `expectPublicId`, when the caller has one, is compared too: a responder that
 * proves a perfectly good key it is not the hub the person chose is still not
 * the hub. What this does *not* claim, honestly: a device that generates a key
 * of its own and names a `publicId` of its own proves exactly that — it is a hub
 * of its own, not this one — and no pre-adoption check can tell it from a
 * brand-new hub, because the cloud has never seen either. That is what the
 * person's own route (the sticker's `h=`) and the hub's single-use claim guard
 * are for; the copier class this refuses is the one the acceptance measures.
 *
 * Same ladder, same refusals, same rule as `proveHubKey`: the rung that proved
 * is returned with the verdict, and the caller dials the token at **that** rung
 * alone.
 */
export async function proveResponderKey(input: ProveResponderKeyInput): Promise<ResponderKeyProof> {
  const doFetch = input.fetchImpl ?? resolveHubFetch();
  const timeoutMs = input.timeoutMs ?? DEFAULT_TIMEOUT_MS;
  const attempts = input.attempts ?? hubApiAttempts(input.port);
  const expected = normalizeHubPublicId(input.expectPublicId ?? '');

  let answeredAndFailed = false;
  let answered = false;
  let detail: string | null = null;

  for (const attempt of attempts) {
    const baseUrl = `${attempt.scheme}://${input.ip}:${attempt.port}/api`;
    const nonce = generateHubChallengeNonce();
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), timeoutMs);
    try {
      const response = await doFetch(`${baseUrl}${HUB_CHALLENGE_PATH}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ nonce }),
        signal: controller.signal,
      });
      // Anything that answers over HTTP is a responder; what it said decides
      // which refusal this is.
      answered = true;

      if (response.status === 404 || response.status === 405) {
        detail = `${baseUrl}: no challenge route (${response.status})`;
        continue;
      }
      if (!response.ok) {
        detail = `${baseUrl}: refused (${response.status})`;
        continue;
      }

      const body = await response.json().catch(() => null);
      const answer = readHubChallengeAnswer(body);
      if (!answer) {
        answeredAndFailed = true;
        detail = `${baseUrl}: malformed`;
        continue;
      }

      const publicId = normalizeHubPublicId(answer.publicId);
      // 1. The id must be the hash of the key that will be asked to sign. A
      //    response that names a real hub's id beside a key of its own dies here.
      const derived = await hubPublicIdFromKey(answer.publicKey);
      if (!derived || derived !== publicId) {
        answeredAndFailed = true;
        detail = `${baseUrl}: publicId ${publicId || '(none)'} is not the hash of the key it names`;
        continue;
      }
      // 2. …and when the caller's route named an identity, it must be this one.
      if (expected && publicId !== expected) {
        answeredAndFailed = true;
        detail = `${baseUrl}: proved ${publicId}, the caller's route named ${expected}`;
        continue;
      }

      const verdict = await verifyHubChallengeAnswer({
        answer,
        nonce,
        publicId,
        publicKey: answer.publicKey,
      });
      if (verdict.ok) {
        return { ok: true, attempt, baseUrl, hubId: answer.hubId, publicId, publicKey: answer.publicKey };
      }

      answeredAndFailed = true;
      detail = `${baseUrl}: ${verdict.reason}`;
    } catch (error) {
      // Timeout, refused connection, DNS — try the next address shape.
      detail = `${baseUrl}: ${error instanceof Error ? error.message : 'no answer'}`;
      continue;
    } finally {
      clearTimeout(timer);
    }
  }

  return {
    ok: false,
    reason: answeredAndFailed ? 'not-proven' : 'cannot-prove',
    answered,
    detail,
  };
}
